/**
 * React Query hooks for AgentBuilder server state.
 *
 * Custom-agent endpoint caches use the generated SDK keys and hold the SDK response shapes; the
 * frontend graph and version shapes are derived from them here. The editor's working copy of the open
 * agent (normalized graph, optimistic name and appearance) lives in the private
 * AGENT_BUILDER_QUERY_KEYS.detail entry, which only explicit editor actions write. UI-only state
 * (selectedNodeId, isDirty, selectedVersionId, draftBaseVersionId) lives in the Zustand store at
 * agentBuilder/agentBuilder.store.ts.
 */

import {
  type NetworkMode,
  type QueryClient,
  type UseMutationOptions,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  AGENT_BUILDER_LIST_PARAMS,
  getAgentBuilderTemplates,
  mapCoreSummaryToListItem,
  mapCustomAgentOutputToAgentBuilder,
  mapCustomAgentVersions,
  pickDefaultSelectedVersion,
  saveAgentBuilderDraft,
  toDirectChatAgent,
  updateAgentBuilderMetadata,
} from '@/agentBuilder/agentBuilder.utilities';
import { logError } from '@/utilities/logger';
import {
  GetCustomAgentTriggerQueryKey,
  GetCustomAgentVersionGraphQueryKey,
  GetCustomAgentVersionsQueryKey,
  GetCustomAgentsProductionEnum,
  GetCustomAgentsQueryKey,
  sqCustomAgentsApi,
  useApiCreateCustomAgentVersionMutation,
  useApiDeleteCustomAgentTriggerMutation,
  useApiDiscardCustomAgentDraftMutation,
  useApiGetCustomAgentTriggerQuery,
  useApiGetCustomAgentVersionGraphQuery,
  useApiGetCustomAgentVersionsQuery,
  useApiGetCustomAgentsQuery,
  useApiPutCustomAgentTriggerMutation,
  useApiSetCustomAgentProductionMutation,
} from '@seeqdev/api/endpoint/CustomAgentsApi';
import { KindEnum as CustomAgentTriggerKindEnum } from '@seeqdev/api/model/CustomAgentTriggerInputV1';
import type { AgentTileAppearanceChange } from '@/agentBuilder/AgentIconTilePicker.molecule';
import type {
  AgentBuilderEdge,
  AgentBuilderGraph,
  AgentBuilderMeta,
  AgentBuilderNode,
  AgentBuilderScheduleTrigger,
  AgentBuilderVersion,
  CoreCustomAgentSummary,
} from '@/agentBuilder/agentBuilder.types';
import type { DirectChatAgent } from '@/agentBuilder/agentBuilder.utilities';
import type { CustomAgentMetadataUpdateInputV1 } from '@seeqdev/api/model/CustomAgentMetadataUpdateInputV1';
import type { CustomAgentSummaryOutputV1 } from '@seeqdev/api/model/CustomAgentSummaryOutputV1';
import type { CustomAgentTriggerOutputV1 } from '@seeqdev/api/model/CustomAgentTriggerOutputV1';

/**
 * Keys for agent builder data that has no generated SDK endpoint. `detail` holds the editor's working
 * copy in the frontend shape; no SDK key starts with 'agent-builder', so canonical invalidations never
 * reach it. Templates are served from /genai.
 */
export const AGENT_BUILDER_QUERY_KEYS = {
  detail: (id: string) => ['agent-builder', id] as const,
  templates: ['agent-builder-templates'] as const,
};

/** Prefix that matches every custom agent list, whatever its filters. */
const CUSTOM_AGENT_LISTS_KEY = GetCustomAgentsQueryKey({});

/** Partial key that matches every cached version graph of one agent. */
const [SDK_NAMESPACE, VERSION_GRAPH_OPERATION] = GetCustomAgentVersionGraphQueryKey({ id: '', versionId: '' });
const versionGraphsKey = (id: string) => [SDK_NAMESPACE, VERSION_GRAPH_OPERATION, { id }] as const;

/** Agents with a production version, offered by the run-agent node's agent selector. */
export const AGENT_BUILDER_PRODUCTION_LIST_PARAMS = {
  ...AGENT_BUILDER_LIST_PARAMS,
  versioned: true,
  production: GetCustomAgentsProductionEnum.Set,
} as const;

// Derived shapes are memoized on the cached response so every subscriber of one cache entry shares
// a stable array or graph, and effects keyed on them do not re-run on unrelated renders.
const memoizeByResponse = <TResponse extends object, TDerived>(derive: (response: TResponse) => TDerived) => {
  const derived = new WeakMap<TResponse, TDerived>();
  return (response: TResponse) => {
    if (!derived.has(response)) derived.set(response, derive(response));
    return derived.get(response) as TDerived;
  };
};
const toAgentBuilderVersions = memoizeByResponse(mapCustomAgentVersions);
const toAgentBuilderGraph = memoizeByResponse(mapCustomAgentOutputToAgentBuilder);

// === Queries ===

type CustomAgentQueryOptions = {
  networkMode?: NetworkMode;
  retry?: boolean;
};

/**
 * Fetch an agent's current graph with a direct GET, or one published version's graph through its
 * canonical version graph cache, and derive the editor graph from it. Always requests fresh data, and
 * resolves null on failure so load, revert, and version-switch flows can show their own error.
 */
export const useFetchAgentBuilder = () => {
  const queryClient = useQueryClient();
  return async (id: string, versionId?: string): Promise<AgentBuilderGraph | null> => {
    try {
      const output = versionId
        ? await queryClient.fetchQuery({
            queryKey: GetCustomAgentVersionGraphQueryKey({ id, versionId }),
            queryFn: async () => (await sqCustomAgentsApi.getCustomAgentVersionGraph({ id, versionId })).data,
          })
        : (await sqCustomAgentsApi.getCustomAgent({ id })).data;
      return mapCustomAgentOutputToAgentBuilder(output);
    } catch (error) {
      logError(`Error fetching agent builder ${id}${versionId ? ` version ${versionId}` : ''}:`, String(error));
      return null;
    }
  };
};

/**
 * Read the editor's working copy of an agent builder. Never fetches: the page normalizes nodes and
 * edges and emits telemetry during its load, create, publish, revert, and version-switch flows, so it
 * owns every full write through useSetAgentBuilderCache. Consumers (page, AiAssistant, Header,
 * TabTitle) only subscribe.
 */
export const useAgentBuilderQuery = (id: string | undefined) =>
  useQuery<AgentBuilderGraph | null>({
    queryKey: AGENT_BUILDER_QUERY_KEYS.detail(id ?? ''),
    queryFn: skipToken,
  });

export const useAgentBuilderTemplatesQuery = (enabled = true) =>
  useQuery({
    queryKey: AGENT_BUILDER_QUERY_KEYS.templates,
    queryFn: getAgentBuilderTemplates,
    enabled,
    staleTime: 30_000,
  });

/** Id lookup memoized on the response array, so all per-message subscribers share one index. */
const customAgentIndexes = new WeakMap<CustomAgentSummaryOutputV1[], Map<string, CustomAgentSummaryOutputV1>>();

const findCustomAgentById = (items: CustomAgentSummaryOutputV1[], id: string) => {
  let index = customAgentIndexes.get(items);
  if (!index) {
    index = new Map(items.map((item) => [item.id, item]));
    customAgentIndexes.set(items, index);
  }
  return index.get(id);
};

/** Read one custom agent out of the shared list cache, scoped by `select` to that id. */
export const useAgentBuilderListItemQuery = (id: string | undefined, enabled = true) => {
  const query = useApiGetCustomAgentsQuery(AGENT_BUILDER_LIST_PARAMS, {
    enabled: enabled && Boolean(id),
    staleTime: 30_000,
    select: (items) => {
      const match = id ? findCustomAgentById(items, id) : undefined;
      return match ? [match] : [];
    },
  });
  const [match] = query.data ?? [];
  return { ...query, data: match ? mapCoreSummaryToListItem(match) : undefined };
};

// Shared by every subscriber of a query, so each failed request is logged once.
const loggedQueryErrors = new WeakSet<Error>();
const useLogQueryError = (error: Error | null, message: string) => {
  useEffect(() => {
    if (error && !loggedQueryErrors.has(error)) {
      loggedQueryErrors.add(error);
      logError(message, String(error));
    }
  }, [error, message]);
};

export const useProductionAgentsQuery = (enabled = true) => {
  const query = useApiGetCustomAgentsQuery(AGENT_BUILDER_PRODUCTION_LIST_PARAMS, { enabled, retry: false });
  useLogQueryError(query.error, 'Error fetching agent builder published:');
  return query;
};

/** Fetch the version history for an agent builder. Newest first. */
export const useAgentBuilderVersionsQuery = (id: string | undefined, options?: CustomAgentQueryOptions) => {
  const query = useApiGetCustomAgentVersionsQuery({ id: id ?? '' }, { retry: false, ...options, enabled: !!id });
  useLogQueryError(query.error, `Error fetching versions for agent builder ${id}:`);
  // Inheriting from React Query's tracked result instead of spreading it means a caller subscribes only
  // to the fields it reads. Object.create returns `any`, so the cast restores the result type.
  return Object.create(query, {
    data: { get: () => (query.data ? toAgentBuilderVersions(query.data) : undefined) },
  }) as Omit<typeof query, 'data'> & { data: AgentBuilderVersion[] | undefined };
};

/** Fetch the schedule trigger for a custom agent. A missing schedule is represented as null. */
export const useAgentBuilderScheduleQuery = (id: string | undefined) => {
  const query = useApiGetCustomAgentTriggerQuery(
    { id: id ?? '' },
    {
      enabled: !!id && id !== 'new',
      // Trigger reads are user-refreshable, so surface failures immediately instead of delaying the
      // UI on retries. A missing schedule is returned as an empty successful response, not a 404.
      retry: false,
    },
  );
  return {
    ...query,
    data: query.data?.kind === CustomAgentTriggerKindEnum.Schedule ? (query.data.schedule ?? null) : null,
  };
};

// === Mutations ===

/** Serializes name and icon writes so they cannot overlap or land out of order. */
const METADATA_MUTATION_SCOPE = { id: 'agent-builder-metadata' } as const;

type UpdateAgentBuilderMetadataVariables<TUpdates extends CustomAgentMetadataUpdateInputV1> = {
  id: string;
  updates: TUpdates;
};

/**
 * Persist name, icon, or color. Callers narrow `TUpdates` so `onError` can read
 * the attempted value without a cast.
 *
 * Throws on null because `updateAgentBuilderMetadata` otherwise never hits
 * `onError`. Never writes the result into the editor (CRAB-57591); callers patch
 * optimistically and roll back in `onError`. On success the canonical caches that
 * embed the name or appearance are marked stale; lists are left in place because
 * the caller already patched them.
 */
export const useUpdateAgentBuilderMetadataMutation = <TUpdates extends CustomAgentMetadataUpdateInputV1>(
  options: Pick<
    UseMutationOptions<AgentBuilderGraph, Error, UpdateAgentBuilderMetadataVariables<TUpdates>>,
    'onSuccess' | 'onError'
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    scope: METADATA_MUTATION_SCOPE,
    mutationFn: async ({ id, updates }: UpdateAgentBuilderMetadataVariables<TUpdates>) => {
      const updated = await updateAgentBuilderMetadata(id, updates);
      if (!updated) {
        throw new Error(`Failed to update agent builder metadata ${id}`);
      }
      return updated;
    },
    onSuccess: (updated, variables, onMutateResult, context) => {
      const { id } = variables;
      void queryClient.invalidateQueries({ queryKey: versionGraphsKey(id) });
      void queryClient.invalidateQueries({ queryKey: CUSTOM_AGENT_LISTS_KEY, refetchType: 'none' });
      return options.onSuccess?.(updated, variables, onMutateResult, context);
    },
    onError: options.onError,
  });
};

/**
 * Apply a saved draft to the editor's working copy and mark the canonical caches it changed as stale.
 *
 * Only graph fields are written: echoing the server's name back into the editor would rewind a title
 * the user is still typing (CRAB-57591), and a separately saved name or icon must not be overwritten.
 */
export const useApplySavedAgentBuilderDraft = () => {
  const queryClient = useQueryClient();
  return ({ id, nodes, edges, meta }: AgentBuilderGraph) => {
    queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) =>
      current ? { ...current, nodes, edges, meta } : current,
    );
    void queryClient.invalidateQueries({ queryKey: CUSTOM_AGENT_LISTS_KEY, refetchType: 'none' });
  };
};

type SaveAgentBuilderDraftVariables = {
  id: string;
  updates: {
    name?: string;
    nodes?: AgentBuilderNode[];
    edges?: AgentBuilderEdge[];
    meta?: AgentBuilderMeta;
  };
};

/** Persist the editor graph as the draft before publishing. */
export const useSaveAgentBuilderDraftMutation = () => {
  const applySavedDraft = useApplySavedAgentBuilderDraft();
  return useMutation({
    mutationFn: ({ id, updates }: SaveAgentBuilderDraftVariables) => saveAgentBuilderDraft(id, updates),
    onSuccess: (data) => {
      if (data) {
        applySavedDraft(data);
      }
    },
  });
};

/** Mark the editor's working copy as having no draft, after a publish or discard. */
const clearAgentBuilderDraftCache = (queryClient: QueryClient, id: string) => {
  queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) =>
    current
      ? { ...current, meta: { ...current.meta, hasDraft: false, draftBaseVersionId: null, draftUpdatedAt: undefined } }
      : current,
  );
};

/**
 * Discard the in-progress draft. Resolves false instead of throwing so callers can show their own error.
 *
 * The editor's working copy is patched draft-free rather than refetched: the callers (revert, version
 * switch) immediately load a specific historical version into it, and a late refetch could clobber
 * that normalized graph.
 */
export const useDiscardAgentBuilderDraftMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useApiDiscardCustomAgentDraftMutation({
    onSuccess: (_data, [{ id }]) => {
      clearAgentBuilderDraftCache(queryClient, id);
      void queryClient.invalidateQueries({ queryKey: GetCustomAgentVersionsQueryKey({ id }) });
      void queryClient.invalidateQueries({ queryKey: CUSTOM_AGENT_LISTS_KEY });
    },
  });
  return {
    mutateAsync: async (id: string): Promise<boolean> => {
      try {
        await mutation.mutateAsync([{ id }]);
        return true;
      } catch (error) {
        logError(`Error discarding draft for agent builder ${id}:`, String(error));
        return false;
      }
    },
  };
};

/**
 * Publish the current draft as a new version. Resolves the refreshed version list, or an empty list
 * on failure.
 *
 * The editor's working copy is patched draft-free rather than refetched, for the same reason as
 * discard: the page writes the post-publish graph into it right after.
 */
export const usePublishAgentBuilderVersionMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useApiCreateCustomAgentVersionMutation({
    onSuccess: (versions, [{ id }]) => {
      queryClient.setQueryData(GetCustomAgentVersionsQueryKey({ id }), versions);
      clearAgentBuilderDraftCache(queryClient, id);
      void queryClient.invalidateQueries({ queryKey: CUSTOM_AGENT_LISTS_KEY });
      // The production version determines whether a saved schedule is effectively enabled,
      // so automation status consumers must refetch after a publish.
      void queryClient.invalidateQueries({ queryKey: GetCustomAgentTriggerQueryKey({ id }) });
    },
  });
  return {
    mutateAsync: async (id: string): Promise<AgentBuilderVersion[]> => {
      try {
        return toAgentBuilderVersions(await mutation.mutateAsync([{ id }]));
      } catch (error) {
        logError(`Error publishing version for agent builder ${id}:`, String(error));
        return [];
      }
    },
  };
};

type SetProductionVariables = {
  id: string;
  versionId: string;
};

/**
 * Mark a specific version as the production version. Resolves the updated version list, or an empty
 * list on failure. Production-filtered lists and direct links follow the new production version.
 */
export const useSetAgentBuilderVersionProductionMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useApiSetCustomAgentProductionMutation({
    onSuccess: (versions, [, { id }]) => {
      queryClient.setQueryData(GetCustomAgentVersionsQueryKey({ id }), versions);
      void queryClient.invalidateQueries({ queryKey: CUSTOM_AGENT_LISTS_KEY });
      // The production version determines whether a saved schedule is effectively enabled,
      // so automation status consumers must refetch after switching production.
      void queryClient.invalidateQueries({ queryKey: GetCustomAgentTriggerQueryKey({ id }) });
    },
  });
  return {
    mutateAsync: async ({ id, versionId }: SetProductionVariables): Promise<AgentBuilderVersion[]> => {
      try {
        return toAgentBuilderVersions(await mutation.mutateAsync([{ versionId }, { id }]));
      } catch (error) {
        logError(`Error setting production for agent builder ${id} to ${versionId}:`, String(error));
        return [];
      }
    },
  };
};

type SaveAgentBuilderScheduleVariables = {
  id: string;
  schedule: Pick<AgentBuilderScheduleTrigger, 'cronSchedule' | 'timezone' | 'enabled'>;
};

/** Create or update the schedule trigger and keep all schedule consumers in sync. */
export const useSaveAgentBuilderScheduleMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useApiPutCustomAgentTriggerMutation({
    onSuccess: (trigger, variables) => {
      const [, { id }] = variables;
      queryClient.setQueryData<CustomAgentTriggerOutputV1>(GetCustomAgentTriggerQueryKey({ id }), trigger);
    },
  });
  return {
    ...mutation,
    mutateAsync: async ({ id, schedule }: SaveAgentBuilderScheduleVariables): Promise<AgentBuilderScheduleTrigger> => {
      const trigger = await mutation.mutateAsync([{ kind: CustomAgentTriggerKindEnum.Schedule, schedule }, { id }]);
      if (trigger.kind !== CustomAgentTriggerKindEnum.Schedule || !trigger.schedule) {
        throw new Error('Agent builder schedule trigger response did not include a schedule');
      }
      return trigger.schedule;
    },
  };
};

/** Delete the schedule trigger and clear it from the shared schedule cache. */
export const useDeleteAgentBuilderScheduleMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useApiDeleteCustomAgentTriggerMutation({
    onSuccess: (_data, variables) => {
      const [{ id }] = variables;
      queryClient.setQueryData<CustomAgentTriggerOutputV1 | null>(GetCustomAgentTriggerQueryKey({ id }), null);
    },
  });
  return {
    ...mutation,
    mutateAsync: async (id: string): Promise<void> => {
      await mutation.mutateAsync([{ id }]);
    },
  };
};

// === Editor working copy ===

/**
 * Imperatively replace the cached agent builder. Used by load/create paths that
 * normalize nodes and edges before handing off to the editor — those paths
 * already have the data in hand and shouldn't trigger a refetch.
 */
export const useSetAgentBuilderCache = () => {
  const queryClient = useQueryClient();
  return (graph: AgentBuilderGraph) => {
    queryClient.setQueryData(AGENT_BUILDER_QUERY_KEYS.detail(graph.id), graph);
  };
};

/**
 * Patch the `name` field on the cached agent builder. History restore can
 * also provide metadata so undo/redo can return to a clean no-draft snapshot
 * without rewriting the rest of the graph.
 */
export const useSetAgentBuilderName = () => {
  const queryClient = useQueryClient();
  return (id: string, name: string, meta?: AgentBuilderMeta) => {
    queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) => {
      if (!current) return current;
      return { ...current, name, ...(meta ? { meta } : {}) };
    });
  };
};

// === Optimistic name and appearance ===

type AgentBuilderListItemPatch = Partial<Pick<CoreCustomAgentSummary, 'name' | 'icon' | 'iconColor'>>;

/** Patch one agent in every cached custom agent list, whatever its filters. */
const patchAgentBuilderListItems = (queryClient: QueryClient, id: string, patch: AgentBuilderListItemPatch) => {
  queryClient.setQueriesData<CoreCustomAgentSummary[] | undefined>({ queryKey: CUSTOM_AGENT_LISTS_KEY }, (current) =>
    current?.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  );
};

/**
 * Read an agent's current appearance: the editor's working copy when one is cached, otherwise the
 * first cached list that contains it.
 */
export const useGetAgentBuilderAppearance = () => {
  const queryClient = useQueryClient();
  return (id: string): AgentTileAppearanceChange | undefined => {
    const editorAgent = queryClient.getQueryData<AgentBuilderGraph | null>(AGENT_BUILDER_QUERY_KEYS.detail(id));
    if (editorAgent) {
      return { icon: editorAgent.icon, iconColor: editorAgent.iconColor };
    }
    for (const [, items] of queryClient.getQueriesData<CustomAgentSummaryOutputV1[]>({
      queryKey: CUSTOM_AGENT_LISTS_KEY,
    })) {
      const item = items?.find((candidate) => candidate.id === id);
      if (item) {
        return { icon: item.icon || undefined, iconColor: item.iconColor || undefined };
      }
    }
    return undefined;
  };
};

export const useSetAgentBuilderAppearance = () => {
  const queryClient = useQueryClient();
  return (id: string, { icon, iconColor }: AgentTileAppearanceChange) => {
    queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) =>
      current ? { ...current, icon, iconColor } : current,
    );
    patchAgentBuilderListItems(queryClient, id, { icon, iconColor });
  };
};

export const useSetAgentBuilderListItemName = () => {
  const queryClient = useQueryClient();
  return (id: string, name: string) => {
    patchAgentBuilderListItems(queryClient, id, { name });
  };
};

// === Direct links ===

type DirectLinkAgent =
  | { status: 'idle' | 'resolving' | 'invalid'; agent?: undefined }
  | { status: 'resolved'; agent: DirectChatAgent };

// A link is opened once, so surface failures immediately rather than retrying behind the loading
// state. Requests go out while offline too, so an offline link reports invalid instead of pausing.
const DIRECT_LINK_QUERY_OPTIONS: CustomAgentQueryOptions = { networkMode: 'always', retry: false };

/**
 * Resolve a direct agent link (`?agentId=`) to the published chat graph it opens: the production
 * version when one is marked, otherwise the latest. Built on the canonical versions and version-graph
 * caches, so a production change made elsewhere in the session is what the next link resolves to.
 */
export const useDirectLinkAgent = (agentId: string | null): DirectLinkAgent => {
  const versionsQuery = useAgentBuilderVersionsQuery(agentId ?? undefined, DIRECT_LINK_QUERY_OPTIONS);
  const versions = versionsQuery.data;
  const targetVersionId = versions ? pickDefaultSelectedVersion(versions)?.versionId : undefined;
  const graphQuery = useApiGetCustomAgentVersionGraphQuery(
    { id: agentId ?? '', versionId: targetVersionId ?? '' },
    { ...DIRECT_LINK_QUERY_OPTIONS, enabled: !!agentId && !!targetVersionId },
  );

  if (!agentId) return { status: 'idle' };
  if (versions && targetVersionId && graphQuery.data) {
    const agent = toDirectChatAgent(toAgentBuilderGraph(graphQuery.data), versions, targetVersionId);
    return agent ? { status: 'resolved', agent } : { status: 'invalid' };
  }
  if (versionsQuery.isPending || versionsQuery.isFetching || graphQuery.isFetching) return { status: 'resolving' };
  return { status: 'invalid' };
};
